import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { type EmployeeTipStats } from '../../../shared/api/employee.service';
import { BaseButton } from '../../../shared/ui';
import {
  formatDashboardCurrency,
  resolveDashboardWithdrawalProgressPercent,
  useHomeDashboardStatementMonth,
} from '../model';

type HomeDashboardStatementCardProps = {
  countryCode: string;
  onOpenStatement?: () => void;
  tipStats: EmployeeTipStats | null;
};

const Heading = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Value = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
`;

const MonthLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ProgressTrack = styled.div`
  height: ${({ theme }) => theme.scale.px(15)};
  border-radius: ${({ theme }) => theme.scale.px(39)};
  background: ${({ theme }) => theme.colors.support.success};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $progressPercent: number }>`
  width: ${({ $progressPercent }) => `${$progressPercent}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.brand.primary};
`;

const Action = styled(BaseButton).attrs({
  'data-ui': 'home-dashboard-statement-card',
})`
  width: 100%;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  text-align: left;
`;

const CardSurface = styled.div`
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(13)};
  box-sizing: border-box;
  display: grid;
  align-content: start;

  & > ${Heading} + ${Value} {
    margin-top: ${({ theme }) => theme.scale.px(13)};
  }

  & > ${Value} + ${MonthLabel} {
    margin-top: ${({ theme }) => theme.scale.px(4)};
  }

  & > ${MonthLabel} + ${ProgressTrack} {
    margin-top: ${({ theme }) => theme.scale.px(10)};
  }
`;

export function HomeDashboardStatementCard({
  countryCode,
  onOpenStatement,
  tipStats,
}: HomeDashboardStatementCardProps) {
  const { t, i18n } = useTranslation();
  const statementMonth = useHomeDashboardStatementMonth(i18n?.language ?? 'en');
  const statementValue = formatDashboardCurrency(
    tipStats?.depositSumMonth,
    countryCode,
  );
  const withdrawalProgressPercent =
    resolveDashboardWithdrawalProgressPercent(tipStats);

  return (
    <Action
      type="button"
      aria-label={t('homeDashboard.statement')}
      onClick={onOpenStatement}
      disabled={!onOpenStatement}
    >
      <CardSurface>
        <Heading>{t('homeDashboard.statement')}</Heading>
        <Value>{statementValue}</Value>
        <MonthLabel>
          {t('homeDashboard.statementMonthLabel', { month: statementMonth })}
        </MonthLabel>
        <ProgressTrack>
          <ProgressFill $progressPercent={withdrawalProgressPercent} />
        </ProgressTrack>
      </CardSurface>
    </Action>
  );
}
